import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

const appendOptional = (formData, key, value) => {
  if (value !== undefined && value !== null) {
    formData.append(key, value);
  }
};

export const fetchPlays = createAsyncThunk('plays/list', (args = {}) => {
  const { status } = args;
  return api.get(`/plays?status=${status ? encodeURIComponent(status) : ''}`).then((response) => response.data);
});

export const createPlay = createAsyncThunk('plays/create', (args, { dispatch }) => {
  const {
    name,
    name_en,
    name_de,
    name_hu,
    type,
    description,
    description_en,
    description_de,
    description_hu,
    length,
    cast,
    portrait,
    cover,
    index_for_search,
    has_breaks,
    min_rec_age,
    attributes,
  } = args;
  const formData = new FormData();
  formData.append('name', name);
  formData.append('name_en', name_en);
  formData.append('name_de', name_de);
  formData.append('name_hu', name_hu);
  formData.append('type', type);
  appendOptional(formData, 'description', description);
  appendOptional(formData, 'description_en', description_en);
  appendOptional(formData, 'description_de', description_de);
  appendOptional(formData, 'description_hu', description_hu);
  for (const artist of cast) {
    formData.append('cast[]', JSON.stringify(artist));
  }
  formData.append('portrait', portrait);
  formData.append('cover', cover);
  formData.append('length', length);
  formData.append('index_for_search', index_for_search);
  formData.append('has_breaks', has_breaks);
  formData.append('min_rec_age', min_rec_age);
  for (const attribute of attributes) {
    formData.append('attributes[]', JSON.stringify(attribute));
  }
  return api.post('/plays', formData).then(() => dispatch(fetchPlays()));
});

export const updatePlay = createAsyncThunk('plays/update', (args, { dispatch }) => {
  const {
    playId,
    name,
    name_en,
    name_de,
    name_hu,
    type,
    status,
    description,
    description_en,
    description_de,
    description_hu,
    length,
    cast,
    portrait,
    cover,
    index_for_search,
    has_breaks,
    min_rec_age,
    apply_to_all_events,
    attributes,
  } = args;
  const formData = new FormData();
  formData.append('name', name);
  formData.append('name_en', name_en);
  formData.append('name_de', name_de);
  formData.append('name_hu', name_hu);
  formData.append('type', type);
  formData.append('status', status);
  appendOptional(formData, 'description', description);
  appendOptional(formData, 'description_en', description_en);
  appendOptional(formData, 'description_de', description_de);
  appendOptional(formData, 'description_hu', description_hu);
  for (const artist of cast) {
    formData.append('cast[]', JSON.stringify(artist));
  }
  formData.append('portrait', portrait);
  formData.append('cover', cover);
  formData.append('index_for_search', index_for_search);
  formData.append('length', length);
  formData.append('has_breaks', has_breaks);
  formData.append('min_rec_age', min_rec_age);
  formData.append('apply_to_all_events', apply_to_all_events);
  for (const attribute of attributes) {
    formData.append('attributes[]', JSON.stringify(attribute));
  }

  return api.patch(`/plays/${playId}`, formData).then(() => dispatch(fetchPlays()));
});

export const deletePlay = createAsyncThunk('plays/delete', (args, { dispatch }) => {
  const { playId } = args;
  return api.delete(`/plays/${playId}`).then(() => dispatch(fetchPlays()));
});

const initialState = {
  data: [],
  error: null,
  isLoading: false,
};

const playsSlice = createSlice({
  name: 'plays',
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchPlays.pending, (state, action) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(fetchPlays.fulfilled, (state, action) => {
      state.isLoading = false;
      state.data = action.payload.data;
    });

    builder.addCase(fetchPlays.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message;
    });

    builder.addCase(createPlay.pending, (state, action) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(createPlay.fulfilled, (state, action) => {
      state.isLoading = false;
    });

    builder.addCase(createPlay.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message;
    });

    builder.addCase(updatePlay.pending, (state, action) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(updatePlay.fulfilled, (state, action) => {
      state.isLoading = false;
    });

    builder.addCase(updatePlay.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message;
    });

    builder.addCase(deletePlay.pending, (state, action) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(deletePlay.fulfilled, (state, action) => {
      state.isLoading = false;
    });

    builder.addCase(deletePlay.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message;
    });
  },
});

export default playsSlice.reducer;
